import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Button,
    Form,
    Row,
    Col,
    Alert,
    Spinner,
    Badge,
    Modal
} from 'react-bootstrap';
import {
    FaArrowLeft,
    FaSave,
    FaPlus,
    FaTrash,
    FaCode,
    FaFileAlt,
    FaEdit
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../../../components/common/Header';
import CourseService from '../../../services/courseService';
import BatchService from '../../../services/batchService';
import AdminNotesService from '../../../services/adminNotesService';
// import CodeBlock from '../../../components/common/CodeBlock';

const EditTopic = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [courses, setCourses] = useState([]);
    const [batches, setBatches] = useState([]);
    
    // Topic Form State
    const [topicData, setTopicData] = useState({
        title: '',
        course_id: '',
        batch_id: '',
        description: '',
        is_published: 1
    });
    
    // Blocks State
    const [blocks, setBlocks] = useState([]);
    const [currentBlock, setCurrentBlock] = useState({
        block_type: 'explanation',
        title: '',
        content: '',
        code_content: '',
        language: 'javascript'
    });
    const [showBlockForm, setShowBlockForm] = useState(false);
    const [editingBlockId, setEditingBlockId] = useState(null);
    
    // Exercises State
    const [exercises, setExercises] = useState([]);
    const [currentExercise, setCurrentExercise] = useState({
        question: '',
        solution: '',
        solution_language: 'javascript',
        difficulty: 'medium',
        show_solution: 0
    });
    const [showExerciseForm, setShowExerciseForm] = useState(false);
    const [editingExerciseId, setEditingExerciseId] = useState(null);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            console.log("Loading topic ID:", id);
            
            // Load courses and batches
            const [courseRes, batchRes, topicRes] = await Promise.all([
                CourseService.getAllCourses(),
                BatchService.getAllBatches(),
                AdminNotesService.getTopic(id)
            ]);
            
            console.log("Topic Response:", topicRes);
            
            // Set courses
            let coursesData = [];
            if (courseRes && courseRes.data && Array.isArray(courseRes.data)) {
                coursesData = courseRes.data;
            }
            setCourses(coursesData);
            
            // Set batches
            setBatches(batchRes.data || []);
            
            // Set topic data
            const topic = topicRes.data.topic;
            setTopicData({
                title: topic.title || '',
                course_id: topic.course_id || '',
                batch_id: topic.batch_id || '',
                description: topic.description || '',
                is_published: topic.is_published || 1
            });
            
            // Set blocks
            setBlocks(topicRes.data.blocks || []);
            
            // Set exercises
            setExercises(topicRes.data.exercises || []);
            
        } catch (error) {
            console.error('Failed to load topic data:', error);
            toast.error('Failed to load topic data: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            loadData();
        }
    }, [id, loadData]);

    const handleTopicChange = (e) => {
        setTopicData({ ...topicData, [e.target.name]: e.target.value });
    };

    // Block Functions
    const openAddBlock = () => {
        setEditingBlockId(null);
        setCurrentBlock({
            block_type: 'explanation',
            title: '',
            content: '',
            code_content: '',
            language: 'javascript'
        });
        setShowBlockForm(true);
    };

    const openEditBlock = (block) => {
        setEditingBlockId(block.id);
        setCurrentBlock({
            block_type: block.block_type,
            title: block.title || '',
            content: block.content || '',
            code_content: block.code_content || '',
            language: block.language || 'javascript'
        });
        setShowBlockForm(true);
    };

    const saveBlock = async () => {
        if (!currentBlock.title && (!currentBlock.content && !currentBlock.code_content)) {
            toast.error('Please add title and content for the block');
            return;
        }
        
        try {
            if (editingBlockId) {
                await AdminNotesService.updateBlock(editingBlockId, {
                    block_type: currentBlock.block_type,
                    title: currentBlock.title,
                    content: currentBlock.content,
                    code_content: currentBlock.code_content,
                    language: currentBlock.language
                });
                toast.success('Block updated successfully');
            } else {
                await AdminNotesService.addBlock({
                    topic_id: id,
                    block_type: currentBlock.block_type,
                    title: currentBlock.title,
                    content: currentBlock.content,
                    code_content: currentBlock.code_content,
                    language: currentBlock.language
                });
                toast.success('Block added successfully');
            }
            
            setShowBlockForm(false);
            loadData();
        } catch (error) {
            toast.error('Failed to save block');
            console.error(error);
        }
    };

    const deleteBlock = async (blockId) => {
        if (window.confirm('Are you sure you want to delete this block?')) {
            try {
                await AdminNotesService.deleteBlock(blockId);
                toast.success('Block deleted successfully');
                loadData();
            } catch (error) {
                toast.error('Failed to delete block');
                console.error(error);
            }
        }
    };

    // Exercise Functions
    const openAddExercise = () => {
        setEditingExerciseId(null);
        setCurrentExercise({
            question: '',
            solution: '',
            solution_language: 'javascript',
            difficulty: 'medium',
            show_solution: 0
        });
        setShowExerciseForm(true);
    };

    const openEditExercise = (exercise) => {
        setEditingExerciseId(exercise.id);
        setCurrentExercise({
            question: exercise.question,
            solution: exercise.solution || '',
            solution_language: exercise.solution_language || 'javascript',
            difficulty: exercise.difficulty,
            show_solution: exercise.show_solution
        });
        setShowExerciseForm(true);
    };

    const saveExercise = async () => {
        if (!currentExercise.question) {
            toast.error('Please enter the exercise question');
            return;
        }
        
        try {
            if (editingExerciseId) {
                await AdminNotesService.updateExercise(editingExerciseId, {
                    question: currentExercise.question,
                    solution: currentExercise.solution,
                    solution_language: currentExercise.solution_language,
                    difficulty: currentExercise.difficulty,
                    show_solution: currentExercise.show_solution ? 1 : 0
                });
                toast.success('Exercise updated successfully');
            } else {
                await AdminNotesService.addExercise({
                    topic_id: id,
                    question: currentExercise.question,
                    solution: currentExercise.solution,
                    solution_language: currentExercise.solution_language,
                    difficulty: currentExercise.difficulty,
                    show_solution: currentExercise.show_solution ? 1 : 0
                });
                toast.success('Exercise added successfully');
            }
            
            setShowExerciseForm(false);
            loadData();
        } catch (error) {
            toast.error('Failed to save exercise');
            console.error(error);
        }
    };

    const deleteExercise = async (exerciseId) => {
        if (window.confirm('Are you sure you want to delete this exercise?')) {
            try {
                await AdminNotesService.deleteExercise(exerciseId);
                toast.success('Exercise deleted successfully');
                loadData();
            } catch (error) {
                toast.error('Failed to delete exercise');
                console.error(error);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!topicData.title.trim()) {
            toast.error('Please enter a topic title');
            return;
        }
        
        if (!topicData.course_id) {
            toast.error('Please select a course');
            return;
        }
        
        setSaving(true);
        
        try {
            await AdminNotesService.updateTopic(id, {
                title: topicData.title,
                description: topicData.description,
                is_published: topicData.is_published
            });
            
            toast.success('Topic updated successfully');
            navigate('/admin/notes');
        } catch (error) {
            toast.error('Failed to update topic');
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div>
                <Header />
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading topic data...</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Header />
            <div className="container-fluid p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <Button
                            variant="link"
                            className="text-decoration-none p-0 mb-2"
                            onClick={() => navigate('/admin/notes')}
                        >
                            <FaArrowLeft className="me-2" /> Back to Notes
                        </Button>
                        <h2 className="mb-0">Edit Topic: {topicData.title}</h2>
                    </div>
                </div>

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col lg={8}>
                            {/* Topic Information */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header className="bg-primary text-white">
                                    <h5 className="mb-0">📝 Topic Information</h5>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Topic Title *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="title"
                                            value={topicData.title}
                                            onChange={handleTopicChange}
                                            required
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            name="description"
                                            rows={3}
                                            value={topicData.description}
                                            onChange={handleTopicChange}
                                        />
                                    </Form.Group>

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Course</Form.Label>
                                                <Form.Select
                                                    value={topicData.course_id}
                                                    disabled
                                                >
                                                    <option value="">Select Course</option>
                                                    {courses.map(course => (
                                                        <option key={course.id} value={course.id}>
                                                            {course.name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <Form.Text className="text-muted">
                                                    Course cannot be changed
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Batch</Form.Label>
                                                <Form.Select
                                                    value={topicData.batch_id}
                                                    disabled
                                                >
                                                    <option value="">All Batches</option>
                                                    {batches.map(batch => (
                                                        <option key={batch.id} value={batch.id}>
                                                            {batch.name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <Form.Text className="text-muted">
                                                    Batch cannot be changed
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group>
                                        <Form.Label>Status</Form.Label>
                                        <div>
                                            <Form.Check
                                                inline
                                                type="radio"
                                                label="Published"
                                                name="is_published"
                                                value={1}
                                                checked={topicData.is_published === 1}
                                                onChange={() => setTopicData({ ...topicData, is_published: 1 })}
                                            />
                                            <Form.Check
                                                inline
                                                type="radio"
                                                label="Draft"
                                                name="is_published"
                                                value={0}
                                                checked={topicData.is_published === 0}
                                                onChange={() => setTopicData({ ...topicData, is_published: 0 })}
                                            />
                                        </div>
                                    </Form.Group>
                                </Card.Body>
                            </Card>

                            {/* Content Blocks */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">📄 Content Blocks ({blocks.length})</h5>
                                    <Button variant="light" size="sm" onClick={openAddBlock}>
                                        <FaPlus className="me-1" /> Add Block
                                    </Button>
                                </Card.Header>
                                <Card.Body>
                                    {blocks.length === 0 ? (
                                        <Alert variant="info">No content blocks added yet.</Alert>
                                    ) : (
                                        blocks.map(block => (
                                            <div key={block.id} className="border rounded p-3 mb-3">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <div>
                                                        {block.block_type === 'explanation' && (
                                                            <Badge bg="info"><FaFileAlt className="me-1" /> Explanation</Badge>
                                                        )}
                                                        {block.block_type === 'code' && (
                                                            <Badge bg="dark"><FaCode className="me-1" /> Code Block</Badge>
                                                        )}
                                                        <strong className="ms-2">{block.title}</strong>
                                                    </div>
                                                    <div>
                                                        <Button variant="outline-warning" size="sm" className="me-1" onClick={() => openEditBlock(block)}>
                                                            <FaEdit /> Edit
                                                        </Button>
                                                        <Button variant="outline-danger" size="sm" onClick={() => deleteBlock(block.id)}>
                                                            <FaTrash />
                                                        </Button>
                                                    </div>
                                                </div>
                                                <div className="text-muted small">
                                                    {block.block_type === 'explanation' ? (
                                                        <div dangerouslySetInnerHTML={{ __html: (block.content || '').substring(0, 100) + '...' }} />
                                                    ) : (
                                                        <code>Code snippet ({block.language})</code>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </Card.Body>
                            </Card>

                            {/* Exercises */}
                            <Card className="mb-4 shadow-sm">
                                <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">✏️ Practice Exercises ({exercises.length})</h5>
                                    <Button variant="light" size="sm" onClick={openAddExercise}>
                                        <FaPlus className="me-1" /> Add Exercise
                                    </Button>
                                </Card.Header>
                                <Card.Body>
                                    {exercises.length === 0 ? (
                                        <Alert variant="info">No exercises added yet.</Alert>
                                    ) : (
                                        exercises.map(exercise => (
                                            <div key={exercise.id} className="border rounded p-3 mb-3">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <Badge bg={exercise.difficulty === 'easy' ? 'success' : exercise.difficulty === 'medium' ? 'warning' : 'danger'}>
                                                            {exercise.difficulty}
                                                        </Badge>
                                                        <strong className="ms-2">{exercise.question.substring(0, 100)}</strong>
                                                    </div>
                                                    <div>
                                                        <Button variant="outline-warning" size="sm" className="me-1" onClick={() => openEditExercise(exercise)}>
                                                            <FaEdit /> Edit
                                                        </Button>
                                                        <Button variant="outline-danger" size="sm" onClick={() => deleteExercise(exercise.id)}>
                                                            <FaTrash />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={4}>
                            <Card className="shadow-sm">
                                <Card.Body>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        size="lg"
                                        className="w-100 mb-2"
                                        disabled={saving}
                                    >
                                        {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="me-2" />}
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="lg"
                                        className="w-100"
                                        onClick={() => navigate('/admin/notes')}
                                    >
                                        Cancel
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            </div>

            {/* Add/Edit Block Modal */}
            <Modal show={showBlockForm} onHide={() => setShowBlockForm(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingBlockId ? 'Edit Block' : 'Add Content Block'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Block Type</Form.Label>
                        <div>
                            <Form.Check
                                inline
                                type="radio"
                                label="Explanation"
                                name="block_type"
                                value="explanation"
                                checked={currentBlock.block_type === 'explanation'}
                                onChange={(e) => setCurrentBlock({ ...currentBlock, block_type: e.target.value })}
                            />
                            <Form.Check
                                inline
                                type="radio"
                                label="Code Block"
                                name="block_type"
                                value="code"
                                checked={currentBlock.block_type === 'code'}
                                onChange={(e) => setCurrentBlock({ ...currentBlock, block_type: e.target.value })}
                            />
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                            type="text"
                            value={currentBlock.title}
                            onChange={(e) => setCurrentBlock({ ...currentBlock, title: e.target.value })}
                        />
                    </Form.Group>

                    {currentBlock.block_type === 'explanation' ? (
                        <Form.Group className="mb-3">
                            <Form.Label>Content (HTML supported)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={10}
                                value={currentBlock.content}
                                onChange={(e) => setCurrentBlock({ ...currentBlock, content: e.target.value })}
                            />
                        </Form.Group>
                    ) : (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Language</Form.Label>
                                <Form.Select
                                    value={currentBlock.language}
                                    onChange={(e) => setCurrentBlock({ ...currentBlock, language: e.target.value })}
                                >
                                    <option value="javascript">JavaScript</option>
                                    <option value="python">Python</option>
                                    <option value="java">Java</option>
                                    <option value="php">PHP</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Code</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={10}
                                    value={currentBlock.code_content}
                                    onChange={(e) => setCurrentBlock({ ...currentBlock, code_content: e.target.value })}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowBlockForm(false)}>Cancel</Button>
                    <Button variant="primary" onClick={saveBlock}>{editingBlockId ? 'Update' : 'Add'}</Button>
                </Modal.Footer>
            </Modal>

            {/* Add/Edit Exercise Modal */}
            <Modal show={showExerciseForm} onHide={() => setShowExerciseForm(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingExerciseId ? 'Edit Exercise' : 'Add Practice Exercise'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Difficulty Level</Form.Label>
                        <div>
                            <Form.Check inline type="radio" label="Easy" name="difficulty" value="easy" checked={currentExercise.difficulty === 'easy'} onChange={(e) => setCurrentExercise({ ...currentExercise, difficulty: e.target.value })} />
                            <Form.Check inline type="radio" label="Medium" name="difficulty" value="medium" checked={currentExercise.difficulty === 'medium'} onChange={(e) => setCurrentExercise({ ...currentExercise, difficulty: e.target.value })} />
                            <Form.Check inline type="radio" label="Hard" name="difficulty" value="hard" checked={currentExercise.difficulty === 'hard'} onChange={(e) => setCurrentExercise({ ...currentExercise, difficulty: e.target.value })} />
                        </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Question</Form.Label>
                        <Form.Control as="textarea" rows={4} value={currentExercise.question} onChange={(e) => setCurrentExercise({ ...currentExercise, question: e.target.value })} required />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Solution Language</Form.Label>
                        <Form.Select value={currentExercise.solution_language} onChange={(e) => setCurrentExercise({ ...currentExercise, solution_language: e.target.value })}>
                            <option value="javascript">JavaScript</option>
                            <option value="python">Python</option>
                            <option value="java">Java</option>
                            <option value="php">PHP</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Solution Code</Form.Label>
                        <Form.Control as="textarea" rows={8} value={currentExercise.solution} onChange={(e) => setCurrentExercise({ ...currentExercise, solution: e.target.value })} />
                    </Form.Group>

                    <Form.Group>
                        <Form.Check type="checkbox" label="Show solution by default" checked={currentExercise.show_solution} onChange={(e) => setCurrentExercise({ ...currentExercise, show_solution: e.target.checked })} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowExerciseForm(false)}>Cancel</Button>
                    <Button variant="primary" onClick={saveExercise}>{editingExerciseId ? 'Update' : 'Add'}</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default EditTopic;